import { useState, useRef, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface FieldMappingDto {
  customer_name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  project_type?: string;
  product_interest?: string;
  estimated_size?: string;
  estimated_budget?: string;
  lead_source?: string;
  notes?: string;
}

interface PreviewResult {
  headers: string[];
  rows: Record<string, string>[];
  totalRows: number;
  suggestedMappings: Partial<FieldMappingDto>;
}

interface ImportResult {
  imported: number;
  errors: Array<{ row: number; reason: string }>;
}

interface ImportLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LEAD_FIELDS = [
  { key: 'customer_name', label: 'Customer Name', required: true },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'mobile', label: 'Mobile', required: false },
  { key: 'address', label: 'Address', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'zip', label: 'ZIP', required: false },
  { key: 'project_type', label: 'Project Type', required: false },
  { key: 'product_interest', label: 'Product Interest', required: false },
  { key: 'estimated_size', label: 'Estimated Size', required: false },
  { key: 'estimated_budget', label: 'Estimated Budget', required: false },
  { key: 'lead_source', label: 'Source', required: false },
  { key: 'notes', label: 'Notes', required: false },
];

export default function ImportLeadsModal({ isOpen, onClose, onSuccess }: ImportLeadsModalProps) {
  const [step, setStep] = useState<'upload' | 'mapping' | 'importing' | 'results'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [fieldMapping, setFieldMapping] = useState<Partial<FieldMappingDto>>({});
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setFieldMapping({});
    setImportResult(null);
    setError('');
    setUploading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleFileSelect = useCallback((selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    setFile(selectedFile);
    setError('');
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/dealer-leads/import/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || 'Upload failed');
      }

      const data: PreviewResult = await response.json();
      setPreview(data);
      setFieldMapping(data.suggestedMappings || {});
      setStep('mapping');
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleMappingChange = (field: string, csvColumn: string) => {
    setFieldMapping((prev) => ({
      ...prev,
      [field]: csvColumn === '__skip__' ? undefined : csvColumn,
    }));
  };

  const canImport = () => {
    return !!fieldMapping.customer_name;
  };

  const handleImport = async () => {
    if (!file || !canImport()) return;

    setError('');
    setStep('importing');

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldMapping', JSON.stringify(fieldMapping));

      const response = await fetch(`${API_BASE}/dealer-leads/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Import failed' }));
        throw new Error(errorData.message || 'Import failed');
      }

      const result: ImportResult = await response.json();
      setImportResult(result);
      setStep('results');

      if (result.imported > 0) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import leads');
      setStep('mapping');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/70" onClick={handleClose} />
        <div className="relative bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
          {/* Header */}
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-white">Import Leads from CSV</h2>
              <p className="text-sm text-gray-400 mt-1">
                {step === 'upload' && 'Step 1: Upload your CSV file'}
                {step === 'mapping' && 'Step 2: Map CSV columns to lead fields'}
                {step === 'importing' && 'Importing leads...'}
                {step === 'results' && 'Import complete'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Step 1: Upload */}
            {step === 'upload' && (
              <div className="space-y-6">
                <div
                  className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                    isDragging
                      ? 'border-green-500 bg-green-500/10'
                      : file
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                  />

                  {file ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <svg className="w-16 h-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium">{file.name}</p>
                        <p className="text-gray-400 text-sm">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setFile(null)}
                        className="text-gray-400 hover:text-white text-sm underline"
                      >
                        Choose a different file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <svg className="w-16 h-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium">Drag and drop your CSV file here</p>
                        <p className="text-gray-400 text-sm">or</p>
                      </div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500"
                      >
                        Browse Files
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        Upload & Preview
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Field Mapping */}
            {step === 'mapping' && preview && (
              <div className="space-y-6">
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-gray-300">
                    Found <span className="font-bold text-white">{preview.totalRows}</span> leads in your CSV file.
                    Map your columns to the lead fields below.
                  </p>
                </div>

                {/* Field Mapping Table */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-white">Field Mapping</h3>
                  <div className="grid gap-3">
                    {LEAD_FIELDS.map((field) => (
                      <div
                        key={field.key}
                        className="grid grid-cols-2 gap-4 items-center bg-gray-700/30 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-white">
                            {field.label}
                            {field.required && <span className="text-red-400 ml-1">*</span>}
                          </span>
                        </div>
                        <select
                          value={fieldMapping[field.key as keyof FieldMappingDto] || '__skip__'}
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                          className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white focus:ring-2 focus:ring-green-500 ${
                            field.required && !fieldMapping[field.key as keyof FieldMappingDto]
                              ? 'border-red-500'
                              : 'border-gray-600'
                          }`}
                        >
                          <option value="__skip__">-- Skip --</option>
                          {preview.headers.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview Table */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-white">Data Preview (First 3 rows)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-700">
                          {LEAD_FIELDS.filter(
                            (f) => fieldMapping[f.key as keyof FieldMappingDto]
                          ).map((field) => (
                            <th key={field.key} className="px-3 py-2 font-medium">
                              {field.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.slice(0, 3).map((row, rowIndex) => (
                          <tr key={rowIndex} className="border-b border-gray-700/50">
                            {LEAD_FIELDS.filter(
                              (f) => fieldMapping[f.key as keyof FieldMappingDto]
                            ).map((field) => {
                              const csvColumn = fieldMapping[field.key as keyof FieldMappingDto];
                              return (
                                <td key={field.key} className="px-3 py-2 text-gray-300">
                                  {csvColumn ? row[csvColumn] || '-' : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-700">
                  <button
                    onClick={() => {
                      setStep('upload');
                      setPreview(null);
                    }}
                    className="px-4 py-2 text-gray-300 hover:text-white"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!canImport()}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Import {preview.totalRows} Leads
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Importing */}
            {step === 'importing' && (
              <div className="py-12 text-center">
                <div className="flex justify-center mb-6">
                  <svg className="animate-spin h-12 w-12 text-green-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-white mb-2">Importing Leads</h3>
                <p className="text-gray-400">Please wait while we import your leads...</p>
              </div>
            )}

            {/* Step 4: Results */}
            {step === 'results' && importResult && (
              <div className="space-y-6">
                <div className="text-center py-8">
                  {importResult.imported > 0 ? (
                    <div className="flex justify-center mb-6">
                      <svg className="h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex justify-center mb-6">
                      <svg className="h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                  )}

                  <h3 className="text-xl font-medium text-white mb-2">
                    {importResult.imported > 0
                      ? 'Import Successful!'
                      : 'Import Completed with Errors'}
                  </h3>

                  <div className="flex justify-center gap-8 mt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-500">{importResult.imported}</p>
                      <p className="text-gray-400 text-sm">Imported</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-red-500">{importResult.errors.length}</p>
                      <p className="text-gray-400 text-sm">Errors</p>
                    </div>
                  </div>
                </div>

                {importResult.errors.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-lg font-medium text-white">Error Details</h4>
                    <div className="bg-gray-700/50 rounded-lg max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-700">
                          <tr className="text-left text-gray-400">
                            <th className="px-4 py-2 font-medium">Row</th>
                            <th className="px-4 py-2 font-medium">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.errors.map((err, index) => (
                            <tr key={index} className="border-t border-gray-600">
                              <td className="px-4 py-2 text-white">{err.row}</td>
                              <td className="px-4 py-2 text-red-400">{err.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4 border-t border-gray-700">
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
